import React from 'react';
import { TextInput as MantineTextInput } from '@mantine/core';

export interface InputProps
  extends Omit<React.ComponentPropsWithoutRef<typeof MantineTextInput>, 'value' | 'onChange'> {
  /** Current value (controlled) */
  value?: string;
  /** Change handler (receives the change event) */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Placeholder text */
  placeholder?: string;
  /** Input type (text, email, password, tel, number, etc.) */
  type?: string;
  /** Label shown above the input */
  label?: React.ReactNode;
  /** Error message (shows below input, applies error styling) */
  error?: React.ReactNode;
  /** Disabled state */
  disabled?: boolean;
  /** Required field indicator */
  required?: boolean;
  /** Input name for forms */
  name?: string;
  /** Optional CSS class name */
  className?: string;
  /** Description or hint below the input */
  description?: React.ReactNode;
}

/**
 * Generic input component. Wraps Mantine TextInput for consistent use across the app.
 * Use for single-line text, email, password, tel, etc.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value = '',
      onChange,
      placeholder,
      type = 'text',
      label,
      error,
      disabled = false,
      required = false,
      name,
      className,
      description,
      ...rest
    },
    ref
  ) => {
    return (
      <MantineTextInput
        ref={ref}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type={type}
        label={label}
        error={error}
        disabled={disabled}
        required={required}
        name={name}
        className={className}
        description={description}
        radius="md"
        size="md"
        {...rest}
      />
    );
  }
);

Input.displayName = 'Input';

export default Input;
